import { FC, FormEvent, useContext, useEffect, useRef, useState } from "react";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import {
  Box,
  Grid,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { UserProgramContext } from "../../../context/user-program/user-program";
import { uploadDocument } from "../../../api/documents";
import { EDocumentCategory } from "../../../content/types";
import { ContainedButton } from "../../styled/contained-button";
import { OutlinedButton } from "../../styled/outlined-button";
import { MAX_UPLOAD_FILE_SIZE } from "../../constants";

type Props = {
  open: boolean;
  onClose: () => void;
  sectionTitle: string;
};

export const NextUploadModal: FC<Props> = ({ open, onClose, sectionTitle }) => {
  const [weight, setWeight] = useState<number | null>(null);
  const [measurementDate, setMeasurementDate] = useState<Dayjs | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { dispatch } = useContext(UserProgramContext);

  const [uploadLoading, setUploadLoading] = useState(false);

  const [error, setError] = useState<string>("");

  const submitNewData = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!file) {
      setError("Bitte wähle eine Datei aus");
      return;
    }

    if (!weight) {
      setError("Bitte gib dein Gewicht ein");
      return;
    }

    if (!measurementDate) {
      setError("Bitte wähle ein Datum aus");
      return;
    }

    setUploadLoading(true);

    uploadDocument({
      title: file.name,
      file,
      sectionTitle,
      weight: `${weight}`,
      measurementDate: `${measurementDate.toDate().getTime()}`,
      category: EDocumentCategory.WEIGHT_DATA,
    })
      .then(() => {
        dispatch({
          type: "UPLOAD_DOCUMENT",
          payload: {
            sectionTitle,
            document: {
              title: file.name,
              category: EDocumentCategory.WEIGHT_DATA,
              createdOn: new Date().getTime(),
              weight: Number(weight),
              measurementDate: measurementDate.toDate().getTime(),
            },
          },
        });
      })
      .finally(() => closeModal());
  };

  const closeModal = () => {
    setError("");
    setUploadLoading(false);
    setFile(null);
    setWeight(0);
    setMeasurementDate(null);
    onClose();
  };

  // @TODO - check by file's hash
  useEffect(() => {
    if (fileInputRef.current && file) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInputRef.current.files = dataTransfer.files;
    }
  }, [file]);

  const formIncomplete = !weight || !measurementDate || !file;

  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: "absolute" as "absolute",
          top: "30%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          border: "2px solid #000",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography id="modal-modal-title" variant="h6" component="h2">
          Neue Datei hochladen
        </Typography>
        <Box
          component="form"
          onSubmit={submitNewData}
          noValidate
          sx={{ mt: 3 }}
        >
          <Grid
            container
            flexDirection="column"
            spacing={3}
            alignItems="center"
          >
            <Grid item xs={12} width="100%">
              <TextField
                fullWidth
                label="Wiegen (kg):"
                variant="outlined"
                value={weight}
                type="number"
                onChange={(e) => {
                  const value = e.target.value;

                  setError("");
                  setWeight(value ? Number(value) : null);
                }}
              />
            </Grid>
            <Grid item xs={12} width="100%">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  disableFuture
                  sx={{ width: "100%" }}
                  label="Gemessen am:"
                  value={measurementDate}
                  onChange={(value) => {
                    setError("");
                    setMeasurementDate(dayjs(value));
                  }}
                />
              </LocalizationProvider>
            </Grid>
            {file && (
              <Grid item xs={12} width="100%">
                <Typography fontWeight="bold">Ausgewählte Datei:</Typography>
                <Typography>{file.name}</Typography>
              </Grid>
            )}
            <Grid item xs={12} width="100%">
              <label htmlFor="weight-upload-doc">
                <input
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  id="weight-upload-doc"
                  name="upload-doc"
                  type="file"
                  multiple={false}
                  onChange={(e) => {
                    setError("");
                    if (e.target.files) {
                      const file = e.target.files[0];

                      if (file.size > MAX_UPLOAD_FILE_SIZE) {
                        setError(
                          "Die Datei ist zu groß. Die maximale Dateigröße beträgt 100 MB"
                        );
                        return;
                      }

                      setFile(file);
                    }
                  }}
                />
                <OutlinedButton fullWidth component="span">
                  Datei Auswählen
                  <FileUploadIcon sx={{ marginLeft: "0.25em" }} />
                </OutlinedButton>
              </label>
            </Grid>
            <Grid item xs={12} width="100%">
              <ContainedButton
                type="submit"
                muiType="loading"
                disabled={formIncomplete || uploadLoading}
                loading={uploadLoading}
                fullWidth
                muiStyleOverride={{ mb: 2 }}
              >
                Fertig
              </ContainedButton>
            </Grid>
            {error && (
              <Grid item xs={12} width="100%">
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};
